/**
 * ExpressionRegistry — Runtime injection prevention for Cogo expressions.
 *
 * Maintains a whitelist of known `#{...}` expressions extracted from the
 * component JSON at load time.  At evaluation time, `isAllowed()` checks
 * whether the expression was pre-registered, preventing dynamically-injected
 * expressions from being executed.
 *
 * Two modes:
 *   strict  = true  → unregistered expressions are rejected (returns false)
 *   strict  = false → unregistered expressions emit a console.warn and pass through
 */

export interface RegistryOptions {
  strict: boolean
}

/** Regex matching the `#{...}` expression wrapper. */
const EXPRESSION_RE = /#{([^}]+)}/g

/**
 * Extract the inner content from a `#{...}` expression string.
 * Returns `null` when the string is not a valid expression wrapper.
 */
function extractInner(expression: string): string | null {
  const match = /^#{(.+)}$/.exec(expression)
  return match ? match[1] : null
}

export class ExpressionRegistry {
  private allowed: Set<string>
  private strict: boolean

  constructor(options?: RegistryOptions) {
    this.allowed = new Set()
    this.strict = options?.strict ?? false
  }

  /**
   * Register a single expression string.
   * Normalizes by extracting the inner content from `#{...}`.
   */
  register(expression: string): void {
    const inner = extractInner(expression)
    if (inner !== null) {
      this.allowed.add(inner)
    }
  }

  /**
   * Recursively walk a JSON value and register every `#{...}` pattern
   * found in string values.
   */
  registerFromJson(json: unknown): void {
    if (json === null || json === undefined) return

    if (typeof json === "string") {
      let match: RegExpExecArray | null
      EXPRESSION_RE.lastIndex = 0
      while ((match = EXPRESSION_RE.exec(json)) !== null) {
        this.allowed.add(match[1])
      }
      return
    }

    if (Array.isArray(json)) {
      for (const item of json) {
        this.registerFromJson(item)
      }
      return
    }

    if (typeof json === "object") {
      for (const value of Object.values(json as Record<string, unknown>)) {
        this.registerFromJson(value)
      }
    }
  }

  /**
   * Register an expression that originates from a trusted runtime source
   * (e.g. an ActionFlow step or a computed value).
   */
  registerDynamic(expression: string, _source: "flow" | "computed"): void {
    this.register(expression)
  }

  /**
   * Check whether an expression is allowed to execute.
   *
   * - Non-expression strings (no `#{...}` wrapper) always pass.
   * - Registered expressions pass.
   * - Unregistered expressions:
   *     strict=true  → rejected (returns false)
   *     strict=false → console.warn + allowed (returns true)
   */
  isAllowed(expression: string): boolean {
    const inner = extractInner(expression)

    // Not a #{...} expression — nothing to gate.
    if (inner === null) return true

    if (this.allowed.has(inner)) return true

    if (this.strict) {
      return false
    }

    console.warn(
      `[ExpressionRegistry] Unregistered expression encountered: ${inner}`
    )
    return true
  }

  /** Number of unique expressions registered. */
  get size(): number {
    return this.allowed.size
  }
}
